import React from 'react';
import NumberFormat from 'react-number-format';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import Radio from '@material-ui/core/Radio';
import { colors, SovosRaisedButton } from 's1-ui';
import TextField from 'commonComponents/textField';
import StateDropdown from 'commonComponents/StateDropDown';
import FBPNAutocomplete from './FBPNAutocomplete';

const { primaryNavy, neutralWhite, buttonOrange } = colors;

const styles = {
  pageContainer: {
    backgroundColor: primaryNavy,
    position: 'fixed',
    overflowY: 'auto',
    height: 'calc(100vh - 30px)',
    width: '100vw',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexFlow: 'column nowrap'
  },
  carrierText: {
    color: neutralWhite,
    fontSize: 35,
    fontWeight: 100,
    paddingTop: 200
  },
  tellUs: {
    color: neutralWhite,
    fontSize: 24,
    fontWeight: 100,
    paddingTop: 40
  },
  radioButtons: {
    color: neutralWhite,
    fontSize: 16,
    fontWeight: 100,
    paddingTop: 60
  },
  FieldRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    width: 400,
    paddingTop: 15
  },
  FBPNAutocomplete: {
    width: 200,
    fontWeight: 400,
    color: neutralWhite
  },
  HomeState: {},
  AccountNumber: {
    '&::placeholder': {
      color: neutralWhite,
      opacity: 1
    }
  },
  button: {
    paddingTop: 30
  },
  buttonText: {
    fontWeight: 100
  },
  radio: {
    color: buttonOrange,
    '&$checked': {
      color: buttonOrange
    }
  },
  checked: {},
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start'
  },
  warning: {
    marginTop: '20px',
    textAlign: 'center',
    color: 'white',
    width: 750
  },
  error: {
    marginTop: '10px',
    textAlign: 'center',
    color: 'red',
  }
};

// eslint-disable-next-line arrow-body-style
const getOnChangeByKey = (onChange, value) => {
  return function onChangeByKey(key) {
    if (key === 'isProducer') {
      value.fbpn = '';
      value.homeState = '';
      value.isUnverifiedFbpn = false;
      value.accountNumber = '';
      value.accountName = '';
    }
    return function mappedOnChange(newValue) {
      return onChange({
        ...value,
        [key]: newValue
      });
    };
  };
};

const isFieldWithInvalidChars = (inputValue) => {
  const regEx = /[^\w-]/g;
  return regEx.test(inputValue);
};

function AccountNumberFormat(props) {
  const { inputRef, onChange, ...other } = props;

  return (
    <NumberFormat
      { ...other }
      getInputRef={ inputRef }
      onValueChange={ (values) => {
        onChange({
          target: {
            value: values.value,
          },
        });
      } }
      decimalScale="0"
      format="#########"
    />
  );
}

/* eslint-disable react/jsx-boolean-value */
export const UnstyledAccountCreatedDisplay = ({
  classes, onSubmit, submitting, onChange, value, onNewFbpn
}) => {
  const getOnChange = getOnChangeByKey(onChange, value);
  const isFbpnInvalid = value.isProducer && value.fbpn !== '' ? isFieldWithInvalidChars(value.fbpn.fbpn) : isFieldWithInvalidChars(value.fbpn);
  const isAccountNumberValid = value.accountNumber.length === 9;
  const isAccountNameValid = value.isProducer || (!value.isProducer && value.accountName);
  return (
    <div className={ `CCP__AccountCreated__Container ${classes.pageContainer}` }>
      <div className={ classes.carrierText }>
        Please complete your setup
      </div>
      <div className={ classes.tellUs }>
        Tell us a little more about yourself
      </div>
      <div className={ classes.formContainer }>
        <div className={ classes.radioButtons }>
          <span>I am a:</span>
          <Radio
            checked={ value.isProducer }
            onChange={ () => getOnChange('isProducer')(true) }
            value={ true }
            classes={ { root: classes.radio, checked: classes.checked } }
            inputProps={ { id: 'producerRadio' } }
          />
          <span>Producer</span>
          <Radio
            checked={ !value.isProducer }
            onChange={ () => getOnChange('isProducer')(false) }
            value={ false }
            classes={ { root: classes.radio, checked: classes.checked } }
            inputProps={ { id: 'retailerRadio' } }
          />
          <span>Retailer</span>
        </div>
        <div className={ classes.FieldRow }>
          { !value.isProducer
            && (
            <TextField
              value={ value.accountName || '' }
              onChange={ e => getOnChange('accountName')(e.target.value) }
              label="Company Name"
              style={ { width: 390 } }
            />
            )
          }
        </div>
        <div className={ classes.FieldRow }>
          <div className={ classes.FBPNAutocomplete }>
            { value.isProducer
              ? (
                <FBPNAutocomplete
                  onChange={ getOnChange('fbpn') }
                  value={ value.fbpn || {} }
                  classes={ classes.FBPNAutocomplete }
                  onNewItem={ onNewFbpn }
                />)
              : (
                <TextField
                  value={ value.fbpn || '' }
                  onChange={ e => getOnChange('fbpn')(e.target.value) }
                  label="Home State License"
                  style={ { width: 200 } }
                />
              )
            }
          </div>
          <div className={ classes.HomeState }>
            <StateDropdown
              onChange={ getOnChange('homeState') }
              value={ value.homeState }
              style={ { width: 150 } }
            />
          </div>
        </div>
        <div className={ classes.FieldRow }>
          <div className={ classes.AccountNumber }>
            <TextField
              value={ value.accountNumber }
              onChange={ e => getOnChange('accountNumber')(e.target.value) }
              label="FedEx Account Number"
              style={ { width: 200 } }
              helperText={ !isAccountNumberValid && '9 characters required' }
              InputProps={ {
                inputComponent: AccountNumberFormat,
              } }
            />
          </div>
        </div>
      </div>
      <div className={ classes.warning }>
        { (value.isUnverifiedFbpn && value.isProducer) && (<div><span role="img" aria-label="warning">⚠️</span><span> The License entered has not been validated. Please double check the License field for accuracy and click  { '"Continue"' } if you wish to proceed with Account Creation.</span></div>) }
      </div>
      <div className={ classes.error }>
        { isFbpnInvalid && (<span>License field has invalid characters, use only letters, numbers and dashes</span>) }
      </div>
      <div className={ classes.button }>
        <SovosRaisedButton
          color={ neutralWhite }
          backgroundColor={ buttonOrange }
          onClick={ onSubmit }
          disabled={ submitting || !value.fbpn || isFbpnInvalid || !value.homeState || !value.accountNumber || !isAccountNumberValid || !isAccountNameValid }
        >
          <span className={ `CCP__AccountCreated__ContinueButton ${classes.buttonText}` }>CONTINUE</span>
        </SovosRaisedButton>
      </div>
    </div>
  );
};

UnstyledAccountCreatedDisplay.propTypes = {
  submitting: PropTypes.bool.isRequired,
  classes: PropTypes.object.isRequired,
  value: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onNewFbpn: PropTypes.func.isRequired,
};

export default withStyles(styles)(UnstyledAccountCreatedDisplay);
